// Авто-скейлер качества: следит за FPS скользящим окном и снимает нагрузку
// ступенями, пока картинка не перестанет тормозить. Когда запас появляется —
// возвращает ступени обратно.
//
// Ступени (снимаются сверху вниз, возвращаются снизу вверх):
//   0 — всё включено
//   1 — без bloom
//   2 — без мягких теней (обычный PCF)
//   3 — без зерна/виньетки
//   4 — pixelRatio 1.5
//   5 — тени выключены совсем (крайний случай)
//
// Ручной выбор игрока (Высокое/Низкое) перекрывает авто — см. setMode().
import * as THREE from 'three';

const WINDOW_MS = 3000;   // окно замера
const DOWN_FPS = 45;      // ниже — снимаем ступень
const UP_FPS = 58;        // стабильно выше — возвращаем
const COOLDOWN_MS = 2500; // пауза между переключениями, чтобы не «дёргало»

const STEP_NAMES = ['всё включено', 'без bloom', 'жёсткие тени', 'без зерна', 'pixelRatio 1.5', 'без теней'];
const MAX_STEP = 5;

export function createQuality({ renderer, onChange }) {
  let step = 0;
  let mode = 'auto';           // auto | high | low
  let frames = 0;
  let windowStart = performance.now();
  let lastSwitch = 0;
  let lastFrame = performance.now();
  let fps = 60;
  let overlay = null;

  // Кадры троттлятся не только от нагрузки: свёрнутый мини-апп, перекрытое
  // окно, переключение чата — браузер режет rAF до 1 Гц. Такие окна замера
  // надо выбрасывать, иначе игрок возвращается в игру без теней и без bloom.
  const STALL_MS = 250;
  function resetWindow(now) {
    frames = 0;
    windowStart = now;
    lastFrame = now;
  }
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) resetWindow(performance.now());
  });

  // Стартовая эвристика: на слабом железе не ждём просадки, а начинаем ниже.
  // deviceMemory есть не везде — отсутствие считаем средним устройством.
  const mem = navigator.deviceMemory || 4;
  const cores = navigator.hardwareConcurrency || 4;
  if (mem <= 2 || cores <= 4) step = 1;

  const state = {
    get bloom() { return step < 1; },
    get softShadows() { return step < 2; },
    get grain() { return step < 3; },
    get shadows() { return step < 5; },
    get crowd() { return step < 2 ? 1 : step < 4 ? 0.5 : 0; }, // множитель толпы зевак
    get pixelRatio() { return step < 4 ? Math.min(window.devicePixelRatio, 2) : 1.5; },
    get step() { return step; },
    get fps() { return Math.round(fps); },
    get preset() { return STEP_NAMES[step]; },
  };

  function apply(reason) {
    renderer.setPixelRatio(state.pixelRatio);
    renderer.shadowMap.enabled = state.shadows;
    renderer.shadowMap.type = state.softShadows ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
    renderer.shadowMap.needsUpdate = true;
    console.log(`[quality] ступень ${step} — ${STEP_NAMES[step]} (${reason}, ${Math.round(fps)} FPS)`);
    if (onChange) onChange(state);
  }

  function setStep(next, reason) {
    const clamped = Math.max(0, Math.min(MAX_STEP, next));
    if (clamped === step) return;
    step = clamped;
    lastSwitch = performance.now();
    apply(reason);
  }

  // Ручной режим игрока перекрывает автоматику
  function setMode(next) {
    mode = next;
    if (mode === 'high') setStep(0, 'выбрано «Высокое»');
    else if (mode === 'low') setStep(4, 'выбрано «Низкое»');
    else apply('вернулись в «Авто»');
  }

  // FPS-оверлей для отладки: ?debug=1
  function enableOverlay() {
    overlay = document.createElement('div');
    overlay.style.cssText =
      'position:fixed;left:8px;top:calc(env(safe-area-inset-top,0px) + 56px);z-index:99;' +
      'background:rgba(0,0,0,.7);color:#0f0;font:11px/1.4 monospace;padding:5px 8px;' +
      'border-radius:6px;pointer-events:none;white-space:pre';
    document.body.appendChild(overlay);
  }
  if (new URLSearchParams(location.search).get('debug') === '1') enableOverlay();

  function update(renderInfo) {
    const now = performance.now();
    // Провал между кадрами = страница была скрыта/заморожена, а не тормозит
    if (now - lastFrame > STALL_MS || document.hidden) {
      resetWindow(now);
      return;
    }
    lastFrame = now;
    frames++;
    const elapsed = now - windowStart;
    if (elapsed < WINDOW_MS) return;

    fps = (frames * 1000) / elapsed;
    frames = 0;
    windowStart = now;

    if (overlay) {
      const calls = renderInfo ? renderInfo.render.calls : 0;
      const tris = renderInfo ? renderInfo.render.triangles : 0;
      overlay.textContent =
        `${Math.round(fps)} FPS  ·  ступень ${step}\n${STEP_NAMES[step]}\n` +
        `draw calls: ${calls}\ntriangles: ${tris.toLocaleString('ru-RU')}`;
      overlay.style.color = fps >= 55 ? '#0f0' : fps >= 45 ? '#ff0' : '#f66';
    }

    if (mode !== 'auto') return;
    if (now - lastSwitch < COOLDOWN_MS) return;

    if (fps < DOWN_FPS && step < MAX_STEP) setStep(step + 1, 'просадка FPS');
    else if (fps > UP_FPS && step > 0) setStep(step - 1, 'есть запас');
  }

  apply('старт');
  return { state, update, setMode, get mode() { return mode; } };
}
